import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../core/redux/store';
import {
  CellPosition,
  CellsReferences,
  NumberFormat,
  NumberFormatMatrix,
  NumberFormats,
  Styles,
  StylesMatrix,
  Sheet,
  SheetView,
  Style,
  ValidationMatrix,
  ValidationAddressMap,
  ValidationRules,
} from '../types';
import { getCell } from '../utils/cells/cellCRUD';
import _ from 'lodash';
import {
  convertArrayAreaAddressToArea,
  convertPositionToArea,
  doAreasIntersect,
  getIntersectedArea,
  isCellInsideArea,
} from '../utils/area/area';

export const selectSheet = (state: RootState, sheetId: string) =>
  state.sheetsManager.sheets[sheetId];

export const selectSheetView = (state: RootState, sheetId: string) =>
  state.sheetsManager.sheetViews[sheetId];

export interface SheetAndView {
  sheet: Sheet;
  sheetView: SheetView;
}
export const selectSheetAndView = (state: RootState, sheetId: string) => ({
  sheet: selectSheet(state, sheetId),
  sheetView: selectSheetView(state, sheetId),
});

export const selectSelectedSheetAreas = (state: RootState, sheetId: string) => {
  const sheetView = selectSheetView(state, sheetId);
  if (sheetView) {
    return sheetView.selections.map((selection) => selection.area);
  }
  return [];
};

export const selectCellStyle = (
  stylesMatrix: StylesMatrix,
  styles: Styles,
  position: CellPosition,
  arrayAddress: string
) => {
  const emptyStyles: Style = {};

  const cellStyles = styles[arrayAddress];
  // const cellRef = getCell(cellsReferences, position);
  // const cellStyles = cellRef?.s;
  if (cellStyles && !_.isEmpty(cellStyles)) return cellStyles;

  const columnMatrix = stylesMatrix[position.ci];
  if (columnMatrix) {
    for (const address of columnMatrix) {
      const area = convertArrayAreaAddressToArea(address);
      if (area && isCellInsideArea(position, area)) {
        return styles[address] ?? emptyStyles;
      }
    }
  }

  return emptyStyles;
};

export const selectCellNumberFormat = (
  numberFormatMatrix: NumberFormatMatrix,
  numberFormats: NumberFormats,
  position: CellPosition,
  arrayAddress: string
) => {
  const cellFormat = numberFormats[arrayAddress];

  // const cellRef = getCell(cellsReferences, position);
  // const cellFormat = cellRef?.nf;
  if (cellFormat) return cellFormat;

  const columnMatrix = numberFormatMatrix[position.ci];
  if (columnMatrix) {
    for (const address of columnMatrix) {
      const area = convertArrayAreaAddressToArea(address);
      if (area && isCellInsideArea(position, area)) {
        return numberFormats[address];
      }
    }
  }

  // TODO: Get Dependent Number Formats
};

export const selectValidationRuleFromPosition = (
  dataValidationMatrix: ValidationMatrix,
  dataValidationMap: ValidationAddressMap,
  dataValidationRules: ValidationRules,
  position: CellPosition,
  arrayAddress: string
) => {
  const cellRuleId = dataValidationMap[arrayAddress];
  if (cellRuleId) {
    return dataValidationRules[cellRuleId];
  }

  const columnMatrix = dataValidationMatrix[position.ci];
  if (columnMatrix) {
    for (const address of columnMatrix) {
      const area = convertArrayAreaAddressToArea(address);
      if (area && isCellInsideArea(position, area)) {
        const ruleId = dataValidationMap[address];
        return dataValidationRules[ruleId];
      }
    }
  }
};
